/* eslint-disable @typescript-eslint/indent */
import { Validators } from "@angular/forms";
import { of } from "rxjs";
import { DropdownQuestion } from "../../../shared/components/dynamic-form/question-dropdown";
import { TextboxQuestion } from "../../../shared/components/dynamic-form/question-textbox";
import { SearchFilterOption } from "../../../shared/components/search-bar/search-filter/search-filter.component";
import { pattern } from "../../../shared/constants/patterns";
import { UserInfo } from "../../../shared/types/user-info";
import { CompanyService } from "../companies/company.service";

export const STATES = [
  {
    label: "Alabama",
    value: "AL",
  },
  {
    label: "Alaska",
    value: "AK",
  },
  {
    label: "American Samoa",
    value: "AS",
  },
  {
    label: "Arizona",
    value: "AZ",
  },
  {
    label: "Arkansas",
    value: "AR",
  },
  {
    label: "California",
    value: "CA",
  },
  {
    label: "Colorado",
    value: "CO",
  },
  {
    label: "Connecticut",
    value: "CT",
  },
  {
    label: "Delaware",
    value: "DE",
  },
  {
    label: "District Of Columbia",
    value: "DC",
  },
  {
    label: "Federated States Of Micronesia",
    value: "FM",
  },
  {
    label: "Florida",
    value: "FL",
  },
  {
    label: "Georgia",
    value: "GA",
  },
  {
    label: "Guam",
    value: "GU",
  },
  {
    label: "Hawaii",
    value: "HI",
  },
  {
    label: "Idaho",
    value: "ID",
  },
  {
    label: "Illinois",
    value: "IL",
  },
  {
    label: "Indiana",
    value: "IN",
  },
  {
    label: "Iowa",
    value: "IA",
  },
  {
    label: "Kansas",
    value: "KS",
  },
  {
    label: "Kentucky",
    value: "KY",
  },
  {
    label: "Louisiana",
    value: "LA",
  },
  {
    label: "Maine",
    value: "ME",
  },
  {
    label: "Marshall Islands",
    value: "MH",
  },
  {
    label: "Maryland",
    value: "MD",
  },
  {
    label: "Massachusetts",
    value: "MA",
  },
  {
    label: "Michigan",
    value: "MI",
  },
  {
    label: "Minnesota",
    value: "MN",
  },
  {
    label: "Mississippi",
    value: "MS",
  },
  {
    label: "Missouri",
    value: "MO",
  },
  {
    label: "Montana",
    value: "MT",
  },
  {
    label: "Nebraska",
    value: "NE",
  },
  {
    label: "Nevada",
    value: "NV",
  },
  {
    label: "New Hampshire",
    value: "NH",
  },
  {
    label: "New Jersey",
    value: "NJ",
  },
  {
    label: "New Mexico",
    value: "NM",
  },
  {
    label: "New York",
    value: "NY",
  },
  {
    label: "North Carolina",
    value: "NC",
  },
  {
    label: "North Dakota",
    value: "ND",
  },
  {
    label: "Northern Mariana Islands",
    value: "MP",
  },
  {
    label: "Ohio",
    value: "OH",
  },
  {
    label: "Oklahoma",
    value: "OK",
  },
  {
    label: "Oregon",
    value: "OR",
  },
  {
    label: "Palau",
    value: "PW",
  },
  {
    label: "Pennsylvania",
    value: "PA",
  },
  {
    label: "Puerto Rico",
    value: "PR",
  },
  {
    label: "Rhode Island",
    value: "RI",
  },
  {
    label: "South Carolina",
    value: "SC",
  },
  {
    label: "South Dakota",
    value: "SD",
  },
  {
    label: "Tennessee",
    value: "TN",
  },
  {
    label: "Texas",
    value: "TX",
  },
  {
    label: "Utah",
    value: "UT",
  },
  {
    label: "Vermont",
    value: "VT",
  },
  {
    label: "Virgin Islands",
    value: "VI",
  },
  {
    label: "Virginia",
    value: "VA",
  },
  {
    label: "Washington",
    value: "WA",
  },
  {
    label: "West Virginia",
    value: "WV",
  },
  {
    label: "Wisconsin",
    value: "WI",
  },
  {
    label: "Wyoming",
    value: "WY",
  },
].map((state) => ({ ...state, value: state.label }));

export function createStateField(countryId: string, value?: string): DropdownQuestion | TextboxQuestion {
  // TODO: review if we can improve this
  const unitedStatesId = "00000000-0000-4000-A000-000000000184";
  return countryId === unitedStatesId
    ? new DropdownQuestion({
        key: "state",
        label: "State",
        value,
        options: STATES,
        search: {
          filter: {
            options: STATES,
            callback: (payload) =>
              of(STATES.filter((state) => state.value.toLowerCase().includes(payload.value.toLowerCase()))),
          },
        },
        required: true,
        order: 8,
      })
    : new TextboxQuestion({
        key: "state",
        label: "State/Province",
        value,
        required: true,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "State/Province is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "State/Province is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "State/Province is <strong>too long</strong>",
          },
        ],
        order: 8,
      });
}

//#region Search Filter Options

export const getLocationSearchFilterOptions = (
  user: UserInfo,
  config?: {
    shown?: string[];
    hidden?: string[];
    companyService?: CompanyService;
  },
): SearchFilterOption<Location>[] => {
  const { shown, hidden, companyService } = config || {};

  return (
    [
      { value: "name", label: "Name" },
      { value: "address1", label: "Address" },
      { value: "city", label: "City" },
      { value: "state", label: "State" },
      { value: "zip", label: "Zip" },
      { value: "name", label: "Country", filterByProperty: { country: { name: true } } },
      {
        auxValue: "companyId",
        value: "company.name",
        label: "Company",
        hidden: user.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }) || !companyService,
        filterByProperty: { company: { name: true } },
      },
    ] as SearchFilterOption<Location>[]
  ).filter((opt) => (shown ? shown.includes(opt.value) : true) && (hidden ? !hidden.includes(opt.value) : true));
};

//#endregion
